import { environment } from 'src/environments/environment';
import { ErrorHandler, NgModule } from '@angular/core';
import {
  BrowserModule,
  HammerModule,
  ɵDomSharedStylesHost,
} from '@angular/platform-browser';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import { OKTA_CONFIG, OktaAuthModule } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { TranslocoRootModule } from './transloco/transloco-root.module';
import { registerLocaleData } from '@angular/common';
import localeEN from '@angular/common/locales/es-US';
import localeES from '@angular/common/locales/pt';
import localePT from '@angular/common/locales/es';
import { FullPageLoadingModule } from './full-page-loading/full-page-loading.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AuthInterceptor } from './shared/interceptors/auth/auth.interceptor';
import { UserGuard } from './shared/guards/user/userGuard';
import { OktaCallbackGuard } from './shared/guards/okta-callback/okta-callback.guard';
import { LogoutGuard } from './shared/guards/logout/logout.guard';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { PagesModule } from './pages/pages.module';
import { ServiceWorkerModule } from '@angular/service-worker';
import { UnexpectedErrorHandler } from './unexpected-error-handler';
import {
  CSP_NONCE_INJECTION_TOKEN,
  CustomDomSharedStylesHost,
} from './custom-dom-shared-styles-host.service';
import { NbThemeModule, NbLayoutModule } from '@nebular/theme';
import { NbEvaIconsModule } from '@nebular/eva-icons';
import { LyTheme2, LY_THEME_NAME, LY_THEME } from '@alyle/ui';
import { MinimaLight, MinimaDark } from '@alyle/ui/themes/minima';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MatNativeDateModule,
} from '@angular/material/core';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { MatDatepickerModule } from '@angular/material/datepicker';

const config = {
  issuer: environment.oktaIssuer,
  clientId: environment.oktaClientId,
  redirectUri: window.location.origin + '/login/callback',
};
const oktaAuth = new OktaAuth(config);

registerLocaleData(localeEN);
registerLocaleData(localeES);
registerLocaleData(localePT);

export const CUSTOM_DATE_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMMM YYYY',
    dateAllyLabel: 'LL',
    monthYearAllyLabel: 'MMMM YYYY',
  },
};

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    TranslocoRootModule,
    AppRoutingModule,
    OktaAuthModule,
    FullPageLoadingModule,
    MatSnackBarModule,
    MatDialogModule,
    MatDividerModule,
    PagesModule,
    BrowserAnimationsModule,
    HammerModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production,
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    NbThemeModule.forRoot({ name: 'default' }),
    NbLayoutModule,
    NbEvaIconsModule,
    MatDatepickerModule,
    MatNativeDateModule,
  ],
  providers: [
    UserGuard,
    OktaCallbackGuard,
    LogoutGuard,
    {
      provide: OKTA_CONFIG,
      useValue: { oktaAuth },
    },
    {
      provide: 'Window',
      useFactory: () => {
        if ((window.document as any).originalCreateElement !== undefined) {
          return window;
        }
        // monkey patches nonce to inline style elements
        (window.document as any).originalCreateElement =
          window.document.createElement; // NOSONAR
        // weird prettier asking for a dot
        // eslint-disable-next-line
        window.document.createElement = function ( // NOSONAR
          tagName: string,
          options: ElementCreationOptions
        ) {
          const newElement = (window.document as any).originalCreateElement(
            tagName,
            options
          );
          if (tagName === 'style') {
            const nonce = document
              .querySelector('meta[name="csp-nonce"]')
              ?.getAttribute('content');
            nonce !== undefined && newElement.setAttribute('nonce', nonce);
          }
          return newElement;
        };
        return window;
      },
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useClass: UnexpectedErrorHandler,
    },
    {
      provide: ɵDomSharedStylesHost,
      useClass: CustomDomSharedStylesHost,
    },
    {
      provide: CSP_NONCE_INJECTION_TOKEN,
      useFactory: () =>
        document
          .querySelector('meta[name="csp-nonce"]')
          ?.getAttribute('content'),
    },
    LyTheme2,
    {
      provide: LY_THEME_NAME,
      useValue: 'minima-light',
    },
    { provide: LY_THEME, useClass: MinimaLight, multi: true },
    { provide: LY_THEME, useClass: MinimaDark, multi: true },

    // Settings below provides a default locale for date formats
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'en-US' },
  ],
  bootstrap: [AppComponent],
})
/**
   TODO: JSDoc
 **/
export class AppModule {}
